
import React from "react";
import { cn } from "@/lib/utils";
import { MetalPrice } from "@/lib/metal-price";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowDown, ArrowUp, Clock } from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { useStore } from "@/store/useStore";
import { getPurityMultiplier, Purity } from "@/lib/pricing";
import { formatPurity } from "@/lib/formatPurity";

interface MetalPriceCardProps {
  metalPrice: MetalPrice | null;
  isLoading?: boolean;
  className?: string;
  metalType: 'gold' | 'silver';
}

export function MetalPriceCard({ metalPrice, isLoading = false, className, metalType }: MetalPriceCardProps) {
  const { setMetalTheme } = useTheme();
  const { goldPurity, silverPurity } = useStore();
  
  // Debug per verificare i valori della purezza
  console.log('[MetalPriceCard] DEBUG - Purity values:', {
    metalType,
    goldPurity,
    silverPurity,
    metalFromPrice: metalPrice?.metal
  });

  if (isLoading || !metalPrice) {
    return (
      <Card className={cn("w-full", className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-xl font-medium capitalize animate-pulse bg-muted h-7 w-24 rounded"></CardTitle>
          <CardDescription className="animate-pulse bg-muted h-4 w-40 rounded"></CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold animate-pulse bg-muted h-9 w-32 rounded"></div>
          <div className="flex items-center mt-2">
            <div className="animate-pulse bg-muted h-5 w-16 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { price, change, changePercentage, timestamp } = metalPrice;
  
  // Use the metalType prop to determine the correct metal (instead of relying on metalPrice.metal)
  const determinedMetal = metalType;
  
  const isPositive = change >= 0;
  const formattedDate = new Date(timestamp).toLocaleString('it-IT');
  
  // Get current purity - con fallback ai valori corretti se non impostati
  let currentPurity: string;
  if (determinedMetal === 'gold') {
    // Forza 24KT per l'oro se il valore nello store non è valido
    currentPurity = goldPurity && goldPurity.includes('KT') ? goldPurity : '24 KT';
  } else {
    currentPurity = silverPurity || 'PURO';
  }
  
  console.log('[MetalPriceCard] Using purity:', {
    determinedMetal,
    currentPurity,
    originalGoldPurity: goldPurity
  });
  
  // Apply purity multiplier to the price based on selected purity
  const purityMultiplier = getPurityMultiplier(
    determinedMetal === 'gold' ? 'ORO' : 'ARGENTO',
    currentPurity as Purity
  );
  
  const adjustedPrice = price * purityMultiplier;
  const adjustedChange = change * purityMultiplier;
  
  const formattedPrice = adjustedPrice.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  const changeFormatted = adjustedChange.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const formattedPurity = formatPurity(determinedMetal, currentPurity);

  const handleClick = () => {
    setMetalTheme(determinedMetal as "gold" | "silver");
  };

  // Determine card styling based on metal type
  const cardStyle = determinedMetal === 'gold' 
    ? 'gold-theme border-gold/50 bg-gold/5 hover:border-gold' 
    : 'silver-theme border-silver/50 bg-silver/5 hover:border-silver';

  const accentStyle = determinedMetal === 'gold' 
    ? 'bg-gold' 
    : 'bg-silver';

  const textStyle = determinedMetal === 'gold' 
    ? 'text-gold-dark dark:text-gold-light' 
    : 'text-silver-dark dark:text-silver-light';

  const metalNames = {
    gold: "Oro",
    silver: "Argento"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        className={cn(
          "w-full transition-all duration-300 hover:shadow-md cursor-pointer overflow-hidden border-2 relative",
          cardStyle,
          className
        )}
        onClick={handleClick}
      >
        <div className={cn("absolute top-0 left-0 w-full h-1", accentStyle)}></div>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle 
              className={cn(
                "text-2xl font-bold capitalize flex items-center",
                textStyle
              )}
            >
              {metalNames[determinedMetal]}
              <Badge 
                variant="outline" 
                className={cn("ml-2", determinedMetal === 'gold' ? "border-gold" : "border-silver")}
              >
                {formattedPurity}
              </Badge>
            </CardTitle>
          </div>
          <CardDescription className="flex items-center gap-1 text-sm text-muted-foreground">
            <Clock className="h-3 w-3" /> {formattedDate}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold mb-2">{formattedPrice}</div>
          <div className={cn(
            "flex items-center text-sm px-2 py-1 rounded-full w-fit",
            isPositive ? "bg-green-600/20 text-green-600" : "bg-red-600/20 text-red-600"
          )}>
            {isPositive ? <ArrowUp className="h-4 w-4 mr-1" /> : <ArrowDown className="h-4 w-4 mr-1" />}
            <span>{changeFormatted} ({changePercentage.toFixed(2)}%)</span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
